import type { DefaultQueryError } from '@ahm/api-wrappers-core';

/**
 * Bản đồ mã lỗi (string code) BE trả về → thông báo tiếng Việt hiển thị cho user.
 *
 * Nguồn: tài liệu error-code-order BE cập nhật cho các API estimate item / estimate
 * order / create order / cancel order. Dùng chung cho toast ở cart-footer,
 * payment-footer (web + webview) và màn hủy đơn (order-list).
 *
 * BE trả `code` ở `error.response.data.code` (enum), còn `title`/`description` là
 * text kỹ thuật nên KHÔNG dùng trực tiếp cho toast — map sang message dưới đây.
 */
export const ORDER_ERROR_MESSAGES: Record<string, string> = {
  // ── Chung: params / auth / hệ thống ──
  INVALID_PARAMETER: 'Thông tin đơn hàng không hợp lệ. Vui lòng kiểm tra lại.',
  NOT_AUTHORIZED: 'Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại.',
  DELIVERY_TOKEN_INVALID: 'Phiên giao hàng không hợp lệ. Vui lòng thử lại.',
  SYSTEM_ERROR: 'Hệ thống đang bận. Vui lòng thử lại sau.',
  SOMETHING_WENT_WRONG: 'Có lỗi xảy ra. Vui lòng thử lại sau.',
  SERVICE_NOT_AVAILABLE: 'Dịch vụ hiện không khả dụng. Vui lòng thử lại sau.',

  // ── Cửa hàng / chi nhánh ──
  NOT_FOUND_STORE: 'Không tìm thấy cửa hàng. Vui lòng thử lại.',
  STORE_CLOSED: 'Cửa hàng đang đóng cửa hoặc ngoài giờ mở cửa.',
  BALANCE_SUSPENDED: 'Cửa hàng tạm ngưng nhận đơn.',
  NOT_FOUND_AVAILABLE_BRANCH:
    'Không tìm thấy chi nhánh phục vụ khu vực này. Vui lòng thử lại sau.',

  // ── Địa chỉ / số điện thoại ──
  NOT_FOUND_ADDRESS:
    'Không xác định được địa chỉ giao hàng. Vui lòng chọn lại địa chỉ.',
  INVALID_PHONE_NUMBER:
    'Số điện thoại không hợp lệ. Vui lòng dùng số điện thoại Việt Nam.',

  // ── Món / tùy chọn / tồn kho ──
  EMPTY_ITEMS_ORDER: 'Giỏ hàng đang trống. Vui lòng chọn món.',
  INVALID_ORDER_ITEM_QUANTITY:
    'Số lượng món không hợp lệ. Vui lòng kiểm tra lại giỏ hàng.',
  NOT_FOUND_ITEM: 'Món ăn không còn khả dụng. Vui lòng chọn món khác.',
  INSUFFICIENT_ITEM:
    'Món đã hết hàng hoặc không đủ số lượng. Vui lòng điều chỉnh giỏ hàng.',
  NOT_FOUND_CUSTOMIZATION:
    'Tùy chọn món không còn khả dụng. Vui lòng chọn lại.',
  ITEM_NOT_CONTAIN_CUSTOMIZATION:
    'Tùy chọn không thuộc món đã chọn. Vui lòng chọn lại.',
  CUSTOMIZATION_QUANTITY_INVALID:
    'Số lượng tùy chọn không hợp lệ. Vui lòng kiểm tra lại.',

  // ── Khuyến mãi ──
  PROMO_UNDER_PROCESSED:
    'Khuyến mãi đang được xử lý. Vui lòng thử lại sau giây lát.',

  // ── Tạo đơn ──
  REQUIRE_ESTIMATED_TOTAL_PAYMENT:
    'Không tính được tổng thanh toán. Vui lòng thử lại.',
  REACH_LIMIT_CREATE_ORDER:
    'Bạn đã đặt quá nhiều đơn trong thời gian ngắn. Vui lòng thử lại sau.',
  NOT_FOUND_CUSTOMER:
    'Không tìm thấy thông tin khách hàng. Vui lòng đăng nhập lại.',
  CUSTOMER_BANNED:
    'Tài khoản của bạn đã bị vô hiệu hóa. Vui lòng liên hệ hotline để được hỗ trợ.',

  // ── Hủy đơn ──
  INVALID_CANCEL_REASON: 'Lý do hủy đơn không hợp lệ.',
  EMPTY_COMMENT_FOR_OTHER_CANCEL_REASON: 'Vui lòng nhập lý do hủy đơn.',
  NOT_FOUND_ORDER: 'Không tìm thấy đơn hàng.',
  INVALID_CANCEL_TIME:
    'Chưa thể hủy đơn. Bạn chỉ có thể hủy sau 20 phút kể từ khi đặt.',
  CAN_NOT_CANCEL_IN_THIS_STATUS:
    'Đơn hàng ở trạng thái hiện tại không thể hủy.',
  INVALID_ROLE: 'Bạn không có quyền thao tác với đơn hàng này.',
  CANCELLATION_ONLY_FOR_PRIORITY_STORE: 'Đơn hàng này hiện chưa thể hủy.',
};

/**
 * Lấy thông báo lỗi tiếng Việt từ error của react-query/axios.
 * Ưu tiên map theo `code` BE trả về; không khớp thì dùng `fallback`.
 */
export function getOrderErrorMessage(
  error: unknown,
  fallback = 'Có lỗi xảy ra. Vui lòng thử lại sau.'
): string {
  const code = (error as DefaultQueryError | undefined)?.response?.data?.code;
  if (code && ORDER_ERROR_MESSAGES[code]) {
    return ORDER_ERROR_MESSAGES[code];
  }
  return fallback;
}
